import { useMemo } from "react";
import { useQuery } from "react-query";

import { Instruction } from "@/v2/entities/instruction/Instruction.model";
import { Prerequisite } from "@/v2/entities/prerequisite/Prerequisite.model";
import { useReleaseApi } from "@/v2/entities/release/Release.api";
import { ReleaseConversation } from "@/v2/entities/release-conversation/ReleaseConversation.model";

import { useSimulator } from "./SimulatorWrapper";

export interface TopicStats {
  topicId: number;
  topicName: string;
  brief: string;
  instructions: Instruction[];
  prerequisites: Prerequisite[];
  conversationsCount: number;
  goodFeedbackCount: number;
  badFeedbackCount: number;
  isEnabled: boolean;
  isKnowledgeEnabled: boolean;
  isHistoricalTicketsEnabled: boolean;
  isWikiEnabled: boolean;
}

export interface ReleaseStats {
  isLoading: boolean;

  instructions: Instruction[];
  prerequisites: Prerequisite[];
  conversationsCount: number;
  goodFeedbackCount: number;
  badFeedbackCount: number;
  topicsCount: number;
  conversations: ReleaseConversation[];

  topics: TopicStats[];
}

export const useReleaseStats = (releaseId: string): ReleaseStats => {
  const { releasesWithVersions, topics } = useSimulator();

  const { getReleaseConversations } = useReleaseApi();

  const { data: conversations, isLoading: isConversationsLoading } = useQuery({
    queryKey: ["release-conversations", releaseId],
    queryFn: () => getReleaseConversations(releaseId.toString()),
    enabled: !!releaseId,
  });

  return useMemo(() => {
    const release = releasesWithVersions?.find(
      (r) => r.id.toString() === releaseId
    );

    if (!release || !topics) {
      return {
        isLoading: true,
        instructions: [],
        prerequisites: [],
        conversationsCount: 0,
        goodFeedbackCount: 0,
        badFeedbackCount: 0,
        topicsCount: 0,
        topics: [],
        conversations: [],
      };
    }

    const allInstructions: Instruction[] = [];
    const allPrerequisites: Prerequisite[] = [];
    const topicStats: TopicStats[] = [];

    topics.forEach((topic) => {
      const topicPolicy = release.topicReleasePolicies?.find(
        (policy) => policy.topicId === topic.id
      );

      const topicInstructions = topicPolicy?.instructions || [];
      const topicPrerequisites = topicPolicy?.prerequisites || [];
      const topicConversations = conversations?.filter(
        (conv) => conv.topicId === topic.id
      );

      allInstructions.push(...topicInstructions);
      allPrerequisites.push(...topicPrerequisites);

      const goodFeedbackCount =
        topicConversations?.filter((conv) => conv.feedback === "POSITIVE")
          .length || 0;
      const badFeedbackCount =
        topicConversations?.filter((conv) => conv.feedback === "NEGATIVE")
          .length || 0;

      topicStats.push({
        topicId: topic.id,
        topicName: topic.topic,
        instructions: topicInstructions,
        prerequisites: topicPrerequisites,
        conversationsCount: topicConversations?.length || 0,
        goodFeedbackCount,
        badFeedbackCount,
        brief: topicPolicy?.brief ?? "",
        isEnabled: topicPolicy?.isEnabled ?? false,
        isKnowledgeEnabled: topicPolicy?.isKnowledgeEnabled ?? false,
        isHistoricalTicketsEnabled:
          topicPolicy?.isHistoricalTicketsEnabled ?? false,
        isWikiEnabled: topicPolicy?.isWikiEnabled ?? false,
      });
    });

    const goodFeedbackCount =
      conversations?.filter((conv) => conv.feedback === "POSITIVE").length || 0;
    const badFeedbackCount =
      conversations?.filter((conv) => conv.feedback === "NEGATIVE").length || 0;

    return {
      isLoading: isConversationsLoading,
      instructions: allInstructions,
      prerequisites: allPrerequisites,
      conversationsCount: conversations?.length || 0,
      goodFeedbackCount,
      badFeedbackCount,
      topicsCount: topics.length,
      topics: topicStats,
      conversations: conversations || [],
    };
  }, [releaseId, releasesWithVersions, topics, conversations]);
};
